import { logger } from '@/lib/logger';

// Core Web Vitals monitoring
// Tracks LCP, FID, CLS, FCP, TTFB

type MetricName = 'LCP' | 'FID' | 'CLS' | 'FCP' | 'TTFB' | 'INP';

interface Metric {
  name: MetricName;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
}

type ReportCallback = (metric: Metric) => void;

// Thresholds based on Google's Core Web Vitals guidelines
const thresholds: Record<MetricName, [number, number]> = {
  LCP: [2500, 4000],    // Good < 2.5s, Poor > 4s
  FID: [100, 300],      // Good < 100ms, Poor > 300ms
  CLS: [0.1, 0.25],     // Good < 0.1, Poor > 0.25
  FCP: [1800, 3000],    // Good < 1.8s, Poor > 3s
  TTFB: [800, 1800],    // Good < 800ms, Poor > 1.8s
  INP: [200, 500],      // Good < 200ms, Poor > 500ms
};

function getRating(name: MetricName, value: number): 'good' | 'needs-improvement' | 'poor' {
  const [good, poor] = thresholds[name];
  if (value <= good) return 'good';
  if (value <= poor) return 'needs-improvement';
  return 'poor';
}

function generateId(): string {
  return `v1-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

// Largest Contentful Paint
export function getLCP(callback: ReportCallback): void {
  if (!('PerformanceObserver' in window)) return;

  const observer = new PerformanceObserver((entryList) => {
    const entries = entryList.getEntries();
    const lastEntry = entries[entries.length - 1] as PerformanceEntry & { startTime: number };
    
    if (lastEntry) {
      const value = lastEntry.startTime;
      callback({
        name: 'LCP',
        value,
        rating: getRating('LCP', value),
        delta: value,
        id: generateId(),
      });
    }
  });

  try {
    observer.observe({ type: 'largest-contentful-paint', buffered: true });
  } catch {
    // Fallback for browsers that don't support buffered flag
  }
}

// First Input Delay
export function getFID(callback: ReportCallback): void {
  if (!('PerformanceObserver' in window)) return;

  const observer = new PerformanceObserver((entryList) => {
    const entries = entryList.getEntries();
    const firstEntry = entries[0] as PerformanceEventTiming;
    
    if (firstEntry) {
      const value = firstEntry.processingStart - firstEntry.startTime;
      callback({
        name: 'FID',
        value,
        rating: getRating('FID', value),
        delta: value,
        id: generateId(),
      });
    }
  });

  try {
    observer.observe({ type: 'first-input', buffered: true });
  } catch {
    // Fallback
  }
}

// Cumulative Layout Shift
export function getCLS(callback: ReportCallback): void {
  if (!('PerformanceObserver' in window)) return;

  let clsValue = 0;
  let sessionValue = 0;
  let sessionEntries: PerformanceEntry[] = [];

  const observer = new PerformanceObserver((entryList) => {
    for (const entry of entryList.getEntries()) {
      const layoutShift = entry as PerformanceEntry & { hadRecentInput: boolean; value: number };
      if (!layoutShift.hadRecentInput) {
        const firstSessionEntry = sessionEntries[0] as PerformanceEntry & { startTime: number } | undefined;
        const lastSessionEntry = sessionEntries[sessionEntries.length - 1] as PerformanceEntry & { startTime: number } | undefined;
        
        if (
          sessionValue &&
          firstSessionEntry &&
          lastSessionEntry &&
          layoutShift.startTime - lastSessionEntry.startTime < 1000 &&
          layoutShift.startTime - firstSessionEntry.startTime < 5000
        ) {
          sessionValue += layoutShift.value;
          sessionEntries.push(entry);
        } else {
          sessionValue = layoutShift.value;
          sessionEntries = [entry];
        }

        if (sessionValue > clsValue) {
          clsValue = sessionValue;
          callback({
            name: 'CLS',
            value: clsValue,
            rating: getRating('CLS', clsValue),
            delta: layoutShift.value,
            id: generateId(),
          });
        }
      }
    }
  });

  try {
    observer.observe({ type: 'layout-shift', buffered: true });
  } catch {
    // Fallback
  }
}

// First Contentful Paint
export function getFCP(callback: ReportCallback): void {
  if (!('PerformanceObserver' in window)) return;

  const observer = new PerformanceObserver((entryList) => {
    const entries = entryList.getEntriesByName('first-contentful-paint');
    const fcpEntry = entries[0] as PerformanceEntry & { startTime: number } | undefined;
    
    if (fcpEntry) {
      const value = fcpEntry.startTime;
      callback({
        name: 'FCP',
        value,
        rating: getRating('FCP', value),
        delta: value,
        id: generateId(),
      });
      observer.disconnect();
    }
  });

  try {
    observer.observe({ type: 'paint', buffered: true });
  } catch {
    // Fallback
  }
}

// Time to First Byte
export function getTTFB(callback: ReportCallback): void {
  if (!('performance' in window)) return;

  const navigationEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
  
  if (navigationEntry) {
    const value = navigationEntry.responseStart - navigationEntry.requestStart;
    callback({
      name: 'TTFB',
      value,
      rating: getRating('TTFB', value),
      delta: value,
      id: generateId(),
    });
  }
}

// Interaction to Next Paint (replaces FID in Core Web Vitals)
export function getINP(callback: ReportCallback): void {
  if (!('PerformanceObserver' in window)) return;

  let maxDuration = 0;

  const observer = new PerformanceObserver((entryList) => {
    for (const entry of entryList.getEntries()) {
      const eventTiming = entry as PerformanceEventTiming;
      if (eventTiming.duration > maxDuration) {
        maxDuration = eventTiming.duration;
        callback({
          name: 'INP',
          value: maxDuration,
          rating: getRating('INP', maxDuration),
          delta: eventTiming.duration,
          id: generateId(),
        });
      }
    }
  });

  try {
    observer.observe({ type: 'event', buffered: true });
  } catch {
    // Fallback
  }
}

// Report all metrics
export function reportWebVitals(onReport?: ReportCallback): void {
  const report: ReportCallback = onReport || ((metric) => {
    // Log in development (logger suppresses in production)
    logger.info(`[Web Vitals] ${metric.name}: ${metric.value.toFixed(2)} (${metric.rating})`);
    
    // Send to analytics in production
    if (import.meta.env.PROD && typeof window !== 'undefined' && 'gtag' in window) {
      const gtag = (window as Window & { gtag?: (...args: unknown[]) => void }).gtag;
      if (gtag) {
        gtag('event', metric.name, {
          event_category: 'Web Vitals',
          event_label: metric.id,
          value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
          non_interaction: true,
        });
      }
    }
  });

  getLCP(report);
  getFID(report);
  getCLS(report);
  getFCP(report);
  getTTFB(report);
  getINP(report);
}
